import { Modal, NewButton, NewButtonVariant } from "@visiblethread/react-components";
import DonutTooltip from "components/DonutTooltip";
import Flex, { ALIGN_CLASS, DIRECTION_CLASS, GAP_CLASS } from "components/Flex";
import {
  ASSIGNEE_COLORS,
  CENTER_LABEL,
  EMPTY_RING_COLOR,
  OTHERS_COLOR,
  SUBTITLE,
  TITLE,
} from "components/ProposalAssignment/constants";
import { modalTypes } from "constants/types";
import { arrayOf, number, shape, string } from "prop-types";
import { useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import {
  exportProposalAssignmentCsv,
  exportProposalAssignmentPdf,
} from "slices/proposalDashboard/proposalAssignment";
import { resolveCssVar } from "utils/cssVar";
import { useDonutChartOptions } from "utils/donutTooltip";

import styles from "./ProposalAssignmentModal.module.scss";

const buildChartData = (allAssignees) => ({
  labels: allAssignees.map((a) => a.displayName),
  datasets: [
    {
      data: allAssignees.map((a) => a.proposalCount),
      backgroundColor: allAssignees.map((_, i) =>
        resolveCssVar(ASSIGNEE_COLORS[i] ?? OTHERS_COLOR)
      ),
      borderWidth: 0,
      hoverOffset: 0,
      hoverBorderWidth: 0,
    },
  ],
});

const buildEmptyChartData = () => ({
  labels: [""],
  datasets: [
    {
      data: [1],
      backgroundColor: [resolveCssVar(EMPTY_RING_COLOR)],
      borderWidth: 0,
      hoverOffset: 0,
      hoverBorderWidth: 0,
    },
  ],
});

const ProposalAssignmentModal = ({ data }) => {
  const dispatch = useDispatch();

  const onDismiss = () => {
    dispatch(hideModal(modalTypes.PROPOSAL_ASSIGNMENT));
  };

  const { totalAssignmentCount, allAssignees = [] } = data;

  const { chartOptions, tooltipRef, titleRef, bodyRef } = useDonutChartOptions({
    formatBody: (item) => item.formattedValue ?? "",
  });

  const chartData = useMemo(() => {
    if (allAssignees.length === 0) return buildEmptyChartData();
    return buildChartData(allAssignees);
  }, [allAssignees]);

  return (
    <Modal
      backdrop
      centered
      className={`fade-in ${styles.modal}`}
      onDismiss={onDismiss}
      show
      size="lg"
    >
      <Modal.Header>{TITLE}</Modal.Header>
      <Modal.Body>
        <Flex
          alignItems={ALIGN_CLASS.stretch}
          direction={DIRECTION_CLASS.column}
          gap={GAP_CLASS.GAP_600}
          className={styles.proposalAssignmentModal}
        >
          <p className={styles.proposalAssignmentModal__subtitle}>{SUBTITLE}</p>
          <div className={styles.proposalAssignmentModal__content}>
            <div className={styles.proposalAssignmentModal__chartWrapper}>
              <div className={styles.proposalAssignmentModal__donutBox}>
                <div className={styles.proposalAssignmentModal__donutCanvas}>
                  <Doughnut data={chartData} options={chartOptions} />
                </div>
                <Flex
                  alignItems={ALIGN_CLASS.center}
                  className={styles.proposalAssignmentModal__donutCenter}
                  direction={DIRECTION_CLASS.column}
                  gap={GAP_CLASS.GAP_050}
                  justifyContent="center"
                >
                  <span className={styles.proposalAssignmentModal__donutValue}>
                    {totalAssignmentCount}
                  </span>
                  <span className={styles.proposalAssignmentModal__donutLabel}>
                    {CENTER_LABEL}
                  </span>
                </Flex>
              </div>
              <DonutTooltip tooltipRef={tooltipRef} titleRef={titleRef} bodyRef={bodyRef} />
            </div>
            <Flex
              alignItems={ALIGN_CLASS.stretch}
              direction={DIRECTION_CLASS.column}
              gap={GAP_CLASS.GAP_300}
              className={styles.proposalAssignmentModal__list}
            >
              {allAssignees.map((assignee, i) => (
                <Flex
                  key={assignee.displayName}
                  alignItems={ALIGN_CLASS.center}
                  gap={GAP_CLASS.GAP_200}
                >
                  <div
                    aria-hidden="true"
                    className={styles.proposalAssignmentModal__legendDot}
                    style={{
                      backgroundColor: resolveCssVar(
                        ASSIGNEE_COLORS[i] ?? OTHERS_COLOR
                      ),
                    }}
                  />
                  <span className={styles.proposalAssignmentModal__name}>
                    {assignee.displayName}
                  </span>
                  <span className={styles.proposalAssignmentModal__count}>
                    {assignee.proposalCount === 1
                      ? "1 proposal"
                      : `${assignee.proposalCount} proposals`}
                  </span>
                </Flex>
              ))}
            </Flex>
          </div>
          <Flex justifyContent="flex-end" gap={GAP_CLASS.GAP_200}>
            <NewButton
              onClick={() => dispatch(exportProposalAssignmentCsv())}
              variant={NewButtonVariant.l2}
            >
              Export CSV
            </NewButton>
            <NewButton
              onClick={() => dispatch(exportProposalAssignmentPdf())}
              variant={NewButtonVariant.l1}
            >
              Export PDF
            </NewButton>
          </Flex>
        </Flex>
      </Modal.Body>
    </Modal>
  );
};

ProposalAssignmentModal.propTypes = {
  data: shape({
    totalAssignmentCount: number.isRequired,
    allAssignees: arrayOf(
      shape({
        displayName: string.isRequired,
        proposalCount: number.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default ProposalAssignmentModal;
